// ============================================================================
// ProjectWizardV3 (Iter32) — Sequential 3-mode wizard
//   • Mode 'complete' : tout d'un coup (Phase 1 + Phase 2)
//   • Mode 'phase1'   : démarrer par le dérush — juste liens + dérusheur + dates dérush
//   • Mode 'phase2'   : compléter un projet 'p1_only' existant — monteur + deadline + formats
// ============================================================================
import { useEffect, useMemo, useState } from 'react';
import { DateModeToggle, RhythmInput, rhythmEndDate } from './RhythmDatesPicker';
import PostageWizard from './PostageWizard';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { formatApiError } from '../lib/utils';
import {
  X, ArrowLeft, ArrowRight, Loader2, Plus, Trash2, Calendar, Users,
  Sparkles, LinkIcon, Folder, Video, AlertTriangle, FileText, Scissors,
  GripVertical,
} from './icons';

const FORMATS = [
  { id: 'podcast', label: 'Podcast' },
  { id: 'facecam', label: 'Facecam' },
  { id: 'short', label: 'Short' },
  { id: 'special', label: 'Spécial' },
];

const CONTENT_TYPES = [
  { id: 'youtube', label: 'YouTube',  desc: 'Long format · pas de Phase 3 (titre + description directement en Phase 2)' },
  { id: 'short',   label: 'Contenu court', desc: 'TikTok / Reels / Shorts · 3 phases complètes' },
  { id: 'ads',     label: 'Publicité (Ads)', desc: 'Format pub · 3 phases complètes' },
];

function Modal({ title, subtitle, onClose, children, maxWidth = 620 }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 100,
      background: 'rgba(10,26,51,0.70)', backdropFilter: 'blur(8px)',
      WebkitBackdropFilter: 'blur(8px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '24px 18px', overflowY: 'auto',
    }}>
      <div onClick={(e) => e.stopPropagation()} className="scale-in" style={{
        width: '100%', maxWidth,
        maxHeight: 'calc(100vh - 48px)', overflowY: 'auto',
        background: 'var(--sf)', border: '1.5px solid var(--bd)', borderRadius: 22,
        padding: 24,
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 18 }}>
          <div style={{ flex: 1 }}>
            <h2 className="asa-heading" style={{ margin: 0, fontSize: 'var(--text-xl)' }}>{title}</h2>
            {subtitle && <div className="asa-eyebrow" style={{ marginTop: 4, color: 'var(--t3)' }}>{subtitle}</div>}
          </div>
          <button data-testid="wizard-close" onClick={onClose}><X size={18} color="var(--t3)" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <div className="asa-eyebrow" style={{ marginBottom: 7 }}>{label}</div>
      <div style={{ padding: '11px 14px', background: 'var(--sf2)', border: '1.5px solid var(--bd)', borderRadius: 12 }}>{children}</div>
      {hint && <div style={{ fontSize: 11.5, color: 'var(--t3)', marginTop: 5, fontFamily: 'Lexend' }}>{hint}</div>}
    </div>
  );
}

function Stepper({ step, total }) {
  return (
    <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
      {Array.from({ length: total }).map((_, i) => (
        <div key={i} data-testid={`wizard-step-dot-${i + 1}`} style={{
          flex: 1, height: 4, borderRadius: 2,
          background: i < step ? '#5BBEFC' : 'var(--bd)',
          transition: 'background 0.25s',
        }} />
      ))}
    </div>
  );
}

// ===========================================================================
export default function ProjectWizardV3({ folderId, users, onClose, onSaved, prefillPhase2ProjectId = null, prefilledClient = '' }) {
  // mode: null | 'complete' | 'phase1' | 'phase2' | 'p2direct'
  //   'p2direct' (Iter40) = create a brand-new project that skips Phase 1 (no dérusheur)
  const [mode, setMode] = useState(prefillPhase2ProjectId ? 'phase2' : null);
  const [step, setStep] = useState(0);
  const [method, setMethod] = useState(null); // 'manual' | 'doc' (complete only)
  const [busy, setBusy] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [docText, setDocText] = useState('');

  // ---- Form fields ----
  const [name, setName] = useState('');
  const [client, setClient] = useState(prefilledClient);
  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [videoCount, setVideoCount] = useState(1);
  const [dateMode, setDateMode] = useState('manual'); // 'manual' | 'rhythm'
  const [rhythm, setRhythm] = useState(null); // { per_slot, every_days, label }
  useEffect(() => {
    if (dateMode === 'rhythm' && rhythm && startDate) setEndDate(rhythmEndDate(startDate, videoCount, rhythm));
  }, [dateMode, rhythm, startDate, videoCount]);
  const [monteurId, setMonteurId] = useState('');
  const [derusheurId, setDerusheurId] = useState('');
  const [driveLinks, setDriveLinks] = useState([{ name: '', url: '' }]);
  const [swiftLinks, setSwiftLinks] = useState([{ name: '', url: '' }]);
  const [videos, setVideos] = useState([{ title: '', format: 'podcast' }]);
  // Iter41 — drag & drop reorder of videos in Step 2
  const [dragFromIdx, setDragFromIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const reorderVideos = (from, to) => {
    if (from === to || from == null || to == null) return;
    setVideos((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  };
  const [contentType, setContentType] = useState('short'); // Iter33: youtube | short | ads
  const [shortCode, setShortCode] = useState(''); // Iter36: 3-5 letter project code used in task titles

  // ---- Phase 2 completion state ----
  const [p1Projects, setP1Projects] = useState([]);
  const [selectedP1, setSelectedP1] = useState(null); // project object once selected
  const [loadingP1, setLoadingP1] = useState(false);
  const [p1Videos, setP1Videos] = useState([]); // existing video tasks of the picked project
  // Iter35 — Phase 2 sub-flow: pick → method-picker → (paste-doc?) → form
  const [phase2Method, setPhase2Method] = useState(null); // 'manual' | 'doc' | null
  const [phase2DocText, setPhase2DocText] = useState('');
  const [phase2DocExtracted, setPhase2DocExtracted] = useState(false);

  // Iter36 — auto-suggest short_code from client (first 3 alpha chars upper)
  // until the user overrides it
  const [shortCodeTouched, setShortCodeTouched] = useState(false);
  useEffect(() => {
    if (shortCodeTouched) return;
    const src = (client || name || '').toUpperCase();
    const letters = src.replace(/[^A-Z]/g, '').slice(0, 3);
    if (letters && letters !== shortCode) setShortCode(letters);
  }, [client, name, shortCodeTouched, shortCode]);

  const monteurs = useMemo(() => users.filter((u) => u.role === 'contributor' || u.is_monteur === true), [users]);

  // Auto-resize videos array when videoCount changes.
  // Iter41 — when new slots are added, alternate format podcast → facecam → …
  // based on the SLOT INDEX (so positions 0,2,4 = podcast and 1,3,5 = facecam).
  // Existing formats already chosen by the user are preserved.
  useEffect(() => {
    setVideos((prev) => {
      const out = [...prev];
      while (out.length < videoCount) {
        const idx = out.length;
        const altFormat = idx % 2 === 0 ? 'podcast' : 'facecam';
        out.push({ title: '', format: altFormat });
      }
      return out.slice(0, videoCount);
    });
  }, [videoCount]);

  // Cadence preview (complete mode)
  const cadence = useMemo(() => {
    if (!startDate || !endDate || videoCount < 1) return null;
    const s = new Date(startDate);
    const e = new Date(endDate);
    const total = Math.max(0, Math.floor((e - s) / 86400000));
    if (videoCount <= 1) return 1;
    return Math.max(1, Math.floor(total / (videoCount - 1)));
  }, [startDate, endDate, videoCount]);

  // Overload preview
  const overloadDates = useMemo(() => {
    if (!cadence || !startDate || videoCount < 2) return [];
    const counts = {};
    const s = new Date(startDate);
    const e = endDate ? new Date(endDate) : null;
    for (let i = 0; i < videoCount; i++) {
      let dt = new Date(s.getTime() + i * cadence * 86400000);
      if (e && dt > e) dt = e;
      const key = dt.toISOString().slice(0, 10);
      counts[key] = (counts[key] || 0) + 1;
    }
    return Object.entries(counts).filter(([, n]) => n >= 2).map(([d]) => d);
  }, [cadence, startDate, endDate, videoCount]);

  // ---- Load p1_only projects when entering phase2 mode ----
  useEffect(() => {
    if (mode !== 'phase2' || selectedP1) return;
    setLoadingP1(true);
    api.get('/projects').then((r) => {
      const list = (r.data || []).filter(
        (p) => p.phase_stage === 'p1_only' && !p.archived,
      );
      setP1Projects(list);
      // Iter34: auto-select if a prefill ID was passed
      if (prefillPhase2ProjectId) {
        const found = list.find((p) => p.id === prefillPhase2ProjectId);
        if (found) {
          setSelectedP1(found);
          setName(found.name || '');
          setClient(found.client || '');
        }
      }
    }).catch(() => toast.error('Erreur chargement projets'))
      .finally(() => setLoadingP1(false));
  }, [mode, selectedP1, prefillPhase2ProjectId]);

  // ---- When a p1 project is selected, fetch its existing video tasks ----
  useEffect(() => {
    if (!selectedP1) return;
    // Iter36 — load short_code from the project so the user can edit it in Phase 2
    if (selectedP1.short_code && !shortCodeTouched) {
      setShortCode(selectedP1.short_code);
      setShortCodeTouched(true); // prevents the client-derived auto-suggest from overriding
    }
    api.get(`/tasks`, { params: { project_id: selectedP1.id } }).then((r) => {
      const tasks = (r.data || []).filter(
        (t) => t.project_id === selectedP1.id && t.kind === 'video',
      );
      tasks.sort((a, b) => (a.video_idx ?? 0) - (b.video_idx ?? 0));
      setP1Videos(tasks);
      // Pre-fill videos editor with existing titles/formats
      setVideos(tasks.map((t) => ({
        title: t.title || '',
        format: t.video_format || 'special',
      })));
    }).catch(() => toast.error('Erreur chargement vidéos'));
  }, [selectedP1]);

  // ---- AI extraction (complete mode) ----
  const runAiExtract = async () => {
    if (docText.trim().length < 20) { toast.error('Texte trop court'); return; }
    setAiLoading(true);
    try {
      const r = await api.post('/projects/from-doc', {
        text: docText.trim(),
        folder: folderId || 'internal',
      });
      const d = r.data;
      if (d.name) setName(d.name);
      if (d.client) setClient(d.client);
      if (d.description) setDescription(d.description);
      if (d.content_type && ['youtube', 'short', 'ads'].includes(d.content_type)) setContentType(d.content_type);
      // Iter33: multi-Drive auto-detect — prefer the new drive_links[] (with format) over legacy drive_link single
      if (Array.isArray(d.drive_links) && d.drive_links.length) {
        setDriveLinks(d.drive_links.map((l) => ({
          name: l.name || `Rush_${l.format || 'Drive'}`,
          url: l.url,
        })));
      } else if (d.drive_link) {
        setDriveLinks([{ name: 'Drive', url: d.drive_link }]);
      }
      if (d.swift_link) setSwiftLinks([{ name: 'SwissTransfer', url: d.swift_link }]);
      if (Array.isArray(d.tasks) && d.tasks.length) {
        setVideoCount(d.tasks.length);
        setVideos(d.tasks.map((t) => ({
          title: t.title || '',
          format: ['podcast', 'facecam', 'short', 'special'].includes(t.format) ? t.format : 'special',
          inspiration_link: t.inspiration_link || '',
          react_link: t.react_link || '',
          tech_instructions: t.tech_instructions || '',
          publication_title: t.publication_title || '',
          publication_description: t.publication_description || '',
        })));
      }
      const detectedDrives = (d.drive_links || []).length;
      const detectedInspi = (d.tasks || []).filter((t) => t.inspiration_link).length;
      const detectedReact = (d.tasks || []).filter((t) => t.react_link).length;
      toast.success(
        `Champs pré-remplis · ${detectedDrives} Drive${detectedDrives > 1 ? 's' : ''}` +
        (detectedInspi ? ` · ${detectedInspi} inspi` : '') +
        (detectedReact ? ` · ${detectedReact} react` : ''),
      );
      setStep(1);
    } catch (e) {
      toast.error(formatApiError(e, 'Erreur IA'));
    } finally {
      setAiLoading(false);
    }
  };

  // Iter35 — AI extract for the Phase 2 sub-flow.
  // Pre-fills content_type, drive_links, swift_links, and the existing video tasks
  // (titles / formats / inspi / react / tech_instructions) so the user only has to
  // verify before submit.
  const runAiExtractPhase2 = async () => {
    if (phase2DocText.trim().length < 20) { toast.error('Texte trop court'); return; }
    setAiLoading(true);
    try {
      const r = await api.post('/projects/from-doc', {
        text: phase2DocText.trim(),
        folder: folderId || 'internal',
      });
      const d = r.data;
      if (d.content_type && ['youtube', 'short', 'ads'].includes(d.content_type)) setContentType(d.content_type);
      if (d.description) setDescription(d.description);
      if (Array.isArray(d.drive_links) && d.drive_links.length) {
        setDriveLinks(d.drive_links.map((l) => ({ name: l.name || `Rush_${l.format || 'Drive'}`, url: l.url })));
      }
      if (d.swift_link) setSwiftLinks([{ name: 'SwissTransfer', url: d.swift_link }]);
      // Map AI tasks to the existing video slots (Iter36: extend if AI found more videos)
      if (Array.isArray(d.tasks) && d.tasks.length) {
        const aiCount = d.tasks.length;
        const existingCount = Math.max(p1Videos.length, videos.length, 1);
        const targetCount = Math.max(aiCount, existingCount);
        const merged = Array.from({ length: targetCount }).map((_, i) => {
          const t = d.tasks[i] || {};
          const existing = videos[i] || {};
          return {
            title: t.title || existing.title || '',
            format: ['podcast', 'facecam', 'short', 'special'].includes(t.format) ? t.format : (existing.format || 'special'),
            inspiration_link: t.inspiration_link || existing.inspiration_link || '',
            react_link: t.react_link || existing.react_link || '',
            tech_instructions: t.tech_instructions || existing.tech_instructions || '',
            description: t.description || existing.description || '',
            publication_title: t.publication_title || existing.publication_title || '',
            publication_description: t.publication_description || existing.publication_description || '',
          };
        });
        setVideos(merged);
      }
      const detectedDrives = (d.drive_links || []).length;
      const detectedInspi = (d.tasks || []).filter((t) => t.inspiration_link).length;
      const detectedReact = (d.tasks || []).filter((t) => t.react_link).length;
      const detectedTitles = (d.tasks || []).filter((t) => t.publication_title).length;
      toast.success(
        `Fiche montage extraite · ${(d.tasks || []).length} vidéo${(d.tasks || []).length > 1 ? 's' : ''}` +
        ` · ${detectedDrives} Drive${detectedDrives > 1 ? 's' : ''}` +
        (detectedTitles ? ` · ${detectedTitles} titre${detectedTitles > 1 ? 's' : ''}` : '') +
        (detectedInspi ? ` · ${detectedInspi} inspi` : '') +
        (detectedReact ? ` · ${detectedReact} react` : '') +
        ' · vérifie & complète',
      );
      setPhase2DocExtracted(true);
    } catch (e) {
      toast.error(formatApiError(e, 'Erreur IA'));
    } finally {
      setAiLoading(false);
    }
  };

  // ---- Validation ----
  const completeStepValid = (s) => {
    if (s === 0) return method !== null;
    if (s === 1) {
      const baseOk = name.trim().length > 0
        && startDate && endDate && endDate >= startDate
        && monteurId
        && videoCount >= 1;
      if (mode === 'p2direct') return baseOk; // no derusheur required
      return baseOk && derusheurId && monteurId !== derusheurId;
    }
    return true;
  };

  const phase1Valid = () => {
    return name.trim().length > 0
      && startDate
      && derusheurId
      && videoCount >= 1
      && shortCode.trim().length >= 2;
  };

  const phase2Valid = () => {
    return selectedP1
      && monteurId && monteurId !== selectedP1.derusheur_id
      && endDate && endDate >= (selectedP1.start_date || '')
      && shortCode.trim().length >= 2;
  };

  // ---- Submit complete project ----
  const submitComplete = async () => {
    if (!completeStepValid(1)) { setStep(1); return; }
    setBusy(true);
    try {
      const cleanDrive = driveLinks.filter((l) => l.url.trim()).map((l) => ({ name: l.name.trim() || 'Drive', url: l.url.trim() }));
      const cleanSwift = swiftLinks.filter((l) => l.url.trim()).map((l) => ({ name: l.name.trim() || 'SwissTransfer', url: l.url.trim() }));
      const payload = {
        name: name.trim(), client: client.trim(),
        folder: folderId || 'internal',
        icon: 'video', color: '#A78BFA',
        description: description.trim(),
        drive_links: cleanDrive, swift_links: cleanSwift,
        monteur_id: monteurId,
        derusheur_id: mode === 'p2direct' ? null : derusheurId,
        start_date: startDate, end_date: endDate,
        video_count: videoCount,
        rhythm_per_slot: dateMode === 'rhythm' && rhythm ? rhythm.per_slot : null,
        rhythm_every_days: dateMode === 'rhythm' && rhythm ? rhythm.every_days : null,
        phase_stage: mode === 'p2direct' ? 'p2_only' : 'complete',
        content_type: contentType,
        short_code: shortCode || null,
        videos: videos.map((v) => ({
          title: v.title.trim(),  // Iter47y — empty triggers backend auto-name "Format N ShortCode"
          format: v.format || 'special',
          inspiration_link: v.inspiration_link || null,
          react_link: v.react_link || null,
          tech_instructions: v.tech_instructions || '',
          publication_title: v.publication_title || null,
          publication_description: v.publication_description || null,
        })),
      };
      const r = await api.post('/projects/v2', payload);
      const overload = (r.data.overload_dates || []).length;
      toast.success(`Projet créé · ${r.data.videos.length} vidéo${r.data.videos.length > 1 ? 's' : ''} (1 / ${r.data.cadence_days}j)${overload ? ` ⚠️ ${overload} jour${overload > 1 ? 's' : ''} en surcharge` : ''}`);
      onSaved(r.data.project);
    } catch (e) {
      toast.error(formatApiError(e, 'Erreur création'));
    } finally {
      setBusy(false);
    }
  };

  // ---- Submit Phase 1 only ----
  const submitPhase1 = async () => {
    if (!phase1Valid()) { toast.error('Champs requis manquants'); return; }
    setBusy(true);
    try {
      const cleanDrive = driveLinks.filter((l) => l.url.trim()).map((l) => ({ name: l.name.trim() || 'Drive', url: l.url.trim() }));
      const cleanSwift = swiftLinks.filter((l) => l.url.trim()).map((l) => ({ name: l.name.trim() || 'SwissTransfer', url: l.url.trim() }));
      const payload = {
        name: name.trim(), client: client.trim(),
        folder: folderId || 'internal',
        icon: 'video', color: '#F59E0B', // orange = Phase 1 en cours
        description: description.trim(),
        drive_links: cleanDrive, swift_links: cleanSwift,
        derusheur_id: derusheurId,
        monteur_id: null,
        start_date: startDate,
        end_date: null,
        video_count: videoCount,
        phase_stage: 'p1_only',
        content_type: contentType,
        short_code: shortCode || null,
        videos: videos.map((v) => ({
          title: v.title.trim(),  // Iter47y — empty triggers backend auto-name
          format: v.format || 'special',
          inspiration_link: v.inspiration_link || null,
          react_link: v.react_link || null,
        })),
      };
      const r = await api.post('/projects/v2', payload);
      toast.success(`Phase 1 lancée · ${r.data.videos.length} dérush${r.data.videos.length > 1 ? 's' : ''} attribué${r.data.videos.length > 1 ? 's' : ''} au dérusheur`);
      onSaved(r.data.project);
    } catch (e) {
      toast.error(formatApiError(e, 'Erreur création'));
    } finally {
      setBusy(false);
    }
  };

  // ---- Submit Phase 2 completion ----
  const submitPhase2 = async () => {
    if (!phase2Valid()) { toast.error('Champs requis manquants'); return; }
    setBusy(true);
    try {
      const cleanDrive = driveLinks.filter((l) => l.url && l.url.trim()).map((l) => ({
        name: (l.name || '').trim() || 'Drive', url: l.url.trim(),
      }));
      const cleanSwift = swiftLinks.filter((l) => l.url && l.url.trim()).map((l) => ({
        name: (l.name || '').trim() || 'SwissTransfer', url: l.url.trim(),
      }));
      const payload = {
        monteur_id: monteurId,
        end_date: endDate,
        content_type: contentType,
        short_code: shortCode || null,
        description: description || null,
        drive_links: cleanDrive,
        swift_links: cleanSwift,
        videos: videos.map((v) => ({
          title: (v.title || '').trim(),  // Iter47y — empty triggers backend auto-name
          format: v.format || 'special',
          inspiration_link: v.inspiration_link || null,
          react_link: v.react_link || null,
          tech_instructions: v.tech_instructions || '',
          description: v.description || '',
          publication_title: v.publication_title || null,
          publication_description: v.publication_description || null,
        })),
      };
      const r = await api.post(`/projects/${selectedP1.id}/complete-phase2`, payload);
      toast.success(`Phase 2 ajoutée · monteur assigné, deadlines calculées`);
      onSaved(r.data);
    } catch (e) {
      toast.error(formatApiError(e, 'Erreur ajout Phase 2'));
    } finally {
      setBusy(false);
    }
  };

  // ========================================================================
  // ROOT — Mode picker (3 cards)
  // ========================================================================
  if (mode === null) {
    return (
      <Modal title="Nouveau projet" subtitle="Choisis comment tu démarres" onClose={onClose} maxWidth={680}>
        <p style={{ fontFamily: 'Lexend', fontSize: 12.5, color: 'var(--t3)', margin: '0 0 18px' }}>
          {`Tu peux créer le projet complet, ou le découper en 2 phases (dérush d'abord, montage plus tard quand t'as plus d'infos).`}
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <ModeCard
            testid="wizard-mode-complete"
            icon={<Video size={20} color="#5BBEFC" />}
            iconBg="rgba(91,190,252,0.15)"
            title="Projet complet (Phase 1 + Phase 2)"
            desc="Je connais déjà le monteur, la deadline et les formats — je remplis tout en une fois."
            onClick={() => { setMode('complete'); setStep(0); }}
          />
          <ModeCard
            testid="wizard-mode-phase1"
            icon={<Scissors size={20} color="#F59E0B" />}
            iconBg="rgba(245,158,11,0.15)"
            title="Démarrer par la Phase 1 (dérush)"
            desc="Je n'ai que les rushs et le dérusheur. Le monteur et la deadline seront ajoutés plus tard."
            onClick={() => { setMode('phase1'); setStep(0); }}
          />
          <ModeCard
            testid="wizard-mode-phase2"
            icon={<Sparkles size={20} color="#A78BFA" />}
            iconBg="rgba(167,139,250,0.15)"
            title="Compléter la Phase 2 d'un projet existant"
            desc="Le dérush avance, je peux maintenant attribuer le monteur, fixer la deadline et préciser les formats."
            onClick={() => { setMode('phase2'); setStep(0); }}
          />
          <ModeCard
            testid="wizard-mode-p2direct"
            icon={<Sparkles size={20} color="#34D399" />}
            iconBg="rgba(52,211,153,0.15)"
            title="Démarrer directement en Phase 2 (sans dérush)"
            desc="Les rushs sont déjà dérushés / pas besoin de dérusheur. Crée le projet en montage direct."
            onClick={() => { setMode('p2direct'); setMethod(null); setStep(0); }}
          />
          <ModeCard
            testid="wizard-mode-postage"
            icon={<Sparkles size={20} color="#F5C56A" />}
            iconBg="rgba(245,197,106,0.15)"
            title="Projet Postage (que des posts)"
            desc="Pas de montage : l'IA lit ton doc (titres, descriptions, liens Drive) et envoie tout direct « À poster » chez le posteur."
            onClick={() => setMode('postage')}
          />
        </div>
        <div style={{ display: 'flex', marginTop: 18 }}>
          <button type="button" onClick={onClose} className="asa-btn-ghost asa-btn">Annuler</button>
        </div>
      </Modal>
    );
  }

  if (mode === 'postage') {
    return <PostageWizard Modal={Modal} Field={Field} users={users} onClose={onClose} onSaved={onSaved} onBack={() => setMode(null)} defaultClient={prefilledClient} />;
  }

  // ========================================================================
  // MODE: phase1 — Single screen
  // ========================================================================
  if (mode === 'phase1') {
    return (
      <Modal title="Phase 1 — Dérush uniquement" subtitle="Phase 2 (monteur, deadlines) sera ajoutée plus tard" onClose={onClose}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Field label="Nom du projet *">
              <input
                data-testid="wizard-name" autoFocus
                value={name} onChange={(e) => setName(e.target.value)}
                placeholder="Ex: Campagne Hakim — Juillet" style={{ width: '100%' }}
              />
            </Field>
            <Field label="Client">
              <input
                data-testid="wizard-client"
                value={client} onChange={(e) => setClient(e.target.value)}
                placeholder="Hakim, Nike…" style={{ width: '100%' }}
              />
            </Field>
          </div>

          <Field label="Description (optionnel)">
            <textarea
              data-testid="wizard-description"
              value={description} onChange={(e) => setDescription(e.target.value)}
              rows={2} style={{ width: '100%', resize: 'vertical', fontFamily: 'Lexend', fontSize: 13 }}
            />
          </Field>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 110px', gap: 10 }}>
            <Field label="Date de début dérush *" hint="1 dérush par jour à partir de cette date">
              <input
                data-testid="wizard-start-date"
                type="date" value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={{ width: '100%', fontFamily: 'Lexend', fontSize: 13, colorScheme: 'dark' }}
              />
            </Field>
            <Field label="Vidéos *">
              <input
                data-testid="wizard-video-count"
                type="number" min={1} max={500}
                value={videoCount}
                onChange={(e) => setVideoCount(Math.max(1, parseInt(e.target.value || '1', 10)))}
                style={{ width: '100%', fontFamily: 'Lexend', fontSize: 13 }}
              />
            </Field>
          </div>

          <Field label="Dérusheur *" hint="Recevra les tâches dérush dans son planning dès création">
            <select
              data-testid="wizard-derusheur"
              value={derusheurId}
              onChange={(e) => setDerusheurId(e.target.value)}
              style={{ width: '100%', fontFamily: 'Lexend', fontSize: 13 }}
            >
              <option value="">— Choisir —</option>
              {monteurs.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </Field>

          <Field label="Code projet (3-5 lettres) *" hint="Sert à nommer les tâches « Vidéo N XXX » dans le planning du monteur">
            <input
              data-testid="wizard-short-code"
              value={shortCode}
              onChange={(e) => { setShortCode(e.target.value.toUpperCase().slice(0, 5)); setShortCodeTouched(true); }}
              placeholder="NCH"
              style={{ width: '100%', fontFamily: 'Sora', fontWeight: 700, fontSize: 14, letterSpacing: 1.5, textTransform: 'uppercase' }}
            />
          </Field>

          <MultiLink
            label="Liens Drive (rushs) *"
            icon={<Folder size={12} />}
            links={driveLinks} setLinks={setDriveLinks}
            placeholder="https://drive.google.com/..."
            testidPrefix="wizard-drive"
            hint="Auto-numérotés à la création (Rush_01_…, Rush_02_…)"
          />
          <MultiLink
            label="Liens SwissTransfer (optionnel)"
            icon={<LinkIcon size={12} />}
            links={swiftLinks} setLinks={setSwiftLinks}
            placeholder="https://www.swisstransfer.com/..."
            testidPrefix="wizard-swift"
          />

          <div data-testid="phase1-info" style={{
            padding: '10px 14px', background: 'rgba(245,158,11,0.10)',
            border: '1.5px solid rgba(245,158,11,0.30)', borderRadius: 12,
            fontFamily: 'Lexend', fontSize: 12.5, color: '#F59E0B',
            display: 'flex', alignItems: 'flex-start', gap: 8,
          }}>
            <Scissors size={14} style={{ marginTop: 2, flexShrink: 0 }} />
            <span>
              Le projet sera créé en mode <strong>Phase 1 uniquement</strong>. Le dérusheur reçoit ses tâches immédiatement.{' '}
              Une fois la Phase 1 terminée (upload Drive de clôture), tu pourras lancer la Phase 2{' '}
              (type de contenu YouTube/Court/Ads, monteur, deadline, formats) via un CTA dédié.
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
          <button
            data-testid="wizard-back" type="button" onClick={() => setMode(null)}
            className="asa-btn-ghost asa-btn"
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
          ><ArrowLeft size={14} /> Retour</button>
          <div style={{ flex: 1 }} />
          <button
            data-testid="wizard-submit-phase1" type="button" onClick={submitPhase1}
            disabled={busy || !phase1Valid()} className="asa-btn"
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
          >
            {busy ? <Loader2 size={14} className="spin" /> : <Scissors size={14} />}
            Lancer la Phase 1
          </button>
        </div>
      </Modal>
    );
  }

  // ========================================================================
  // MODE: phase2 — pick project then complete
  // ========================================================================
  if (mode === 'phase2') {
    // Sub-step A: pick a p1_only project
    if (!selectedP1) {
      return (
        <Modal title="Compléter la Phase 2" subtitle="Sélectionne un projet en Phase 1" onClose={onClose}>
          {loadingP1 ? (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <Loader2 size={20} className="spin" />
            </div>
          ) : p1Projects.length === 0 ? (
            <div data-testid="phase2-empty" style={{
              padding: '24px 18px', background: 'var(--sf2)', borderRadius: 12,
              fontFamily: 'Lexend', fontSize: 13, color: 'var(--t3)', textAlign: 'center',
            }}>
              Aucun projet en Phase 1 pour le moment.<br />
              <span style={{ fontSize: 12 }}>Lance d&apos;abord un projet en mode &laquo; Démarrer par la Phase 1 &raquo;.</span>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 460, overflowY: 'auto' }}>
              {p1Projects.map((p) => (
                <button
                  key={p.id}
                  data-testid={`phase2-pick-${p.id}`}
                  type="button"
                  onClick={() => {
                    setSelectedP1(p);
                    setName(p.name || '');
                    setClient(p.client || '');
                  }}
                  style={{
                    textAlign: 'left', padding: '14px 16px',
                    background: 'var(--sf2)', border: '1.5px solid var(--bd)',
                    borderRadius: 12, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: 12,
                  }}
                >
                  <div style={{
                    width: 38, height: 38, borderRadius: 9,
                    background: 'rgba(245,158,11,0.15)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}><Scissors size={16} color="#F59E0B" /></div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 13.5, color: 'var(--t1)' }}>{p.name}</div>
                    <div style={{ fontFamily: 'Lexend', fontSize: 11.5, color: 'var(--t3)', marginTop: 2 }}>
                      {p.client || '—'} · {p.video_count || 0} vidéo{(p.video_count || 0) > 1 ? 's' : ''} · démarré {(p.start_date || '').slice(0, 10)}
                    </div>
                  </div>
                  <ArrowRight size={16} color="var(--t3)" />
                </button>
              ))}
            </div>
          )}
          <div style={{ display: 'flex', marginTop: 18 }}>
            <button
              type="button" onClick={() => setMode(null)}
              className="asa-btn-ghost asa-btn"
              style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            ><ArrowLeft size={14} /> Retour</button>
          </div>
        </Modal>
      );
    }

    // Sub-step B: method picker (manuel ou depuis un doc) — Iter35
    if (phase2Method === null) {
      return (
        <Modal
          title={`Phase 2 · ${selectedP1.name}`}
          subtitle="Comment veux-tu compléter la fiche montage ?"
          onClose={onClose}
        >
          <p style={{ fontFamily: 'Lexend', fontSize: 12.5, color: 'var(--t3)', margin: '0 0 18px' }}>
            La phase 1 (dérush) est terminée. Pose maintenant ta <strong>fiche montage</strong> pour démarrer la Phase 2.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <button
              data-testid="wizard-phase2-method-doc"
              type="button" onClick={() => setPhase2Method('doc')}
              style={{
                textAlign: 'left', padding: '16px 18px',
                background: 'var(--sf2)', border: '1.5px solid var(--bd)',
                borderRadius: 14, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 14,
              }}
            >
              <div style={{
                width: 42, height: 42, borderRadius: 11,
                background: 'rgba(167,139,250,0.15)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}><FileText size={18} color="#A78BFA" /></div>
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 14, color: 'var(--t1)' }}>Depuis un document</div>
                <div style={{ fontFamily: 'Lexend', fontSize: 12, color: 'var(--t3)', marginTop: 2, lineHeight: 1.4 }}>
                  Colle ton brief client (fiche montage). L&apos;IA extrait le type de contenu, les Drive, les titres/formats, et les liens d&apos;inspi/react.
                </div>
              </div>
              <ArrowRight size={16} color="var(--t3)" />
            </button>
            <button
              data-testid="wizard-phase2-method-manual"
              type="button" onClick={() => setPhase2Method('manual')}
              style={{
                textAlign: 'left', padding: '16px 18px',
                background: 'var(--sf2)', border: '1.5px solid var(--bd)',
                borderRadius: 14, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 14,
              }}
            >
              <div style={{
                width: 42, height: 42, borderRadius: 11,
                background: 'rgba(91,190,252,0.15)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}><Video size={18} color="#5BBEFC" /></div>
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 14, color: 'var(--t1)' }}>Création manuelle</div>
                <div style={{ fontFamily: 'Lexend', fontSize: 12, color: 'var(--t3)', marginTop: 2, lineHeight: 1.4 }}>
                  Je remplis les champs à la main (type, monteur, deadline, titres).
                </div>
              </div>
              <ArrowRight size={16} color="var(--t3)" />
            </button>
          </div>
          <div style={{ display: 'flex', marginTop: 18 }}>
            <button
              type="button" onClick={() => { setSelectedP1(null); setP1Videos([]); }}
              className="asa-btn-ghost asa-btn"
              style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            ><ArrowLeft size={14} /> Retour</button>
          </div>
        </Modal>
      );
    }

    // Sub-step C: paste-doc (only if method === 'doc' and not yet extracted) — Iter35
    if (phase2Method === 'doc' && !phase2DocExtracted) {
      return (
        <Modal
          title={`Phase 2 · ${selectedP1.name}`}
          subtitle="Colle ton brief client (fiche montage)"
          onClose={onClose}
        >
          <p style={{ fontFamily: 'Lexend', fontSize: 12.5, color: 'var(--t3)', margin: '0 0 14px' }}>
            L&apos;IA va extraire automatiquement : <strong>type de contenu</strong>, <strong>liens Drive par format</strong>,{' '}
            <strong>titres / formats des vidéos</strong>, et les <strong>liens d&apos;inspiration / react</strong>.
            Tu vérifies ensuite avant validation.
          </p>
          <textarea
            data-testid="wizard-phase2-doc-text"
            value={phase2DocText}
            onChange={(e) => setPhase2DocText(e.target.value)}
            placeholder="Colle ici le brief client (concepts, drives, formats, inspi, react…)"
            rows={10}
            style={{
              width: '100%', padding: '11px 13px',
              background: 'var(--sf2)', border: '1.5px solid var(--bd)',
              borderRadius: 11, color: 'var(--t1)',
              fontFamily: 'Lexend', fontSize: 13, resize: 'vertical',
            }}
          />
          <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
            <button
              type="button" onClick={() => setPhase2Method(null)}
              className="asa-btn-ghost asa-btn"
              style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            ><ArrowLeft size={14} /> Retour</button>
            <div style={{ flex: 1 }} />
            <button
              data-testid="wizard-phase2-doc-extract"
              type="button" onClick={runAiExtractPhase2}
              disabled={aiLoading || phase2DocText.trim().length < 20}
              className="asa-btn"
              style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            >
              {aiLoading ? <Loader2 size={14} className="spin" /> : <Sparkles size={14} />}
              Extraire & pré-remplir
            </button>
          </div>
        </Modal>
      );
    }

    // Sub-step D: complete phase 2 form (verify & complete)
    return (
      <Modal
        title={`Phase 2 · ${selectedP1.name}`}
        subtitle={phase2Method === 'doc' ? 'Vérifie & complète la fiche extraite' : 'Ajoute monteur, deadline et formats'}
        onClose={onClose}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div data-testid="phase2-recap" style={{
            padding: '10px 14px', background: 'var(--sf2)',
            border: '1.5px solid var(--bd)', borderRadius: 12,
            fontFamily: 'Lexend', fontSize: 12.5, color: 'var(--t2)',
          }}>
            <div><strong>Client :</strong> {selectedP1.client || '—'}</div>
            <div><strong>Dérusheur :</strong> {users.find((u) => u.id === selectedP1.derusheur_id)?.name || '—'}</div>
            <div><strong>Début :</strong> {(selectedP1.start_date || '').slice(0, 10)} · <strong>{p1Videos.length}</strong> vidéo{p1Videos.length > 1 ? 's' : ''}</div>
          </div>

          <ContentTypePicker value={contentType} onChange={setContentType} />

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 110px 1fr', gap: 10 }}>
            <Field label="Monteur *" hint="Phase 2 — montage final">
              <select
                data-testid="wizard-monteur"
                value={monteurId}
                onChange={(e) => setMonteurId(e.target.value)}
                style={{ width: '100%', fontFamily: 'Lexend', fontSize: 13 }}
              >
                <option value="">— Choisir —</option>
                {monteurs.filter((u) => u.id !== selectedP1.derusheur_id).map((u) => (
                  <option key={u.id} value={u.id}>{u.name}</option>
                ))}
              </select>
            </Field>
            <Field label="Code *" hint="Vidéo N XXX">
              <input
                data-testid="wizard-short-code"
                value={shortCode}
                onChange={(e) => { setShortCode(e.target.value.toUpperCase().slice(0, 5)); setShortCodeTouched(true); }}
                placeholder="NCH"
                style={{ width: '100%', fontFamily: 'Sora', fontWeight: 700, fontSize: 14, letterSpacing: 1.5, textTransform: 'uppercase' }}
              />
            </Field>
            <Field label="Deadline finale *">
              <input
                data-testid="wizard-end-date"
                type="date" value={endDate} min={selectedP1.start_date}
                onChange={(e) => setEndDate(e.target.value)}
                style={{ width: '100%', fontFamily: 'Lexend', fontSize: 13, colorScheme: 'dark' }}
              />
            </Field>
          </div>

          {/* Iter35 — Drive / Swift links visibles & éditables (issus de l'IA ou ajoutés manuellement) */}
          <MultiLink
            label="Liens Drive (rushs / sources)"
            icon={<Folder size={12} />}
            links={driveLinks} setLinks={setDriveLinks}
            placeholder="https://drive.google.com/..."
            testidPrefix="wizard-phase2-drive"
            hint={phase2Method === 'doc' ? 'Pré-remplis par l\'IA · ajuste si besoin' : ''}
          />
          <MultiLink
            label="Liens SwissTransfer (optionnel)"
            icon={<LinkIcon size={12} />}
            links={swiftLinks} setLinks={setSwiftLinks}
            placeholder="https://www.swisstransfer.com/..."
            testidPrefix="wizard-phase2-swift"
          />

          <div>
            <div className="asa-eyebrow" style={{ marginBottom: 8 }}>
              Vidéos · <strong style={{ color: 'var(--t1)' }}>{videos.length}</strong>
              <span style={{ color: 'var(--t3)', fontWeight: 400 }}>
                {' '} (titre auto = « Vidéo N {shortCode || 'XXX'} »)
                {' '} · <span style={{ color: '#5BBEFC' }}>glisse pour réordonner</span>
              </span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 400, overflowY: 'auto', paddingRight: 4 }}>
              {videos.map((v, i) => (
                <div
                  key={i}
                  data-testid={`wizard-phase2-video-row-${i}`}
                  draggable
                  onDragStart={(e) => {
                    setDragFromIdx(i);
                    e.dataTransfer.effectAllowed = 'move';
                    e.dataTransfer.setData('text/plain', String(i));
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'move';
                    if (dragOverIdx !== i) setDragOverIdx(i);
                  }}
                  onDragLeave={() => {
                    setDragOverIdx((prev) => (prev === i ? null : prev));
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    const from = dragFromIdx ?? parseInt(e.dataTransfer.getData('text/plain'), 10);
                    reorderVideos(from, i);
                    setDragFromIdx(null);
                    setDragOverIdx(null);
                  }}
                  onDragEnd={() => { setDragFromIdx(null); setDragOverIdx(null); }}
                  style={{
                    padding: '10px 12px', background: 'var(--sf2)',
                    border: dragOverIdx === i && dragFromIdx !== i
                      ? '1px dashed #5BBEFC'
                      : '1px solid var(--bd)',
                    borderRadius: 10,
                    display: 'flex', flexDirection: 'column', gap: 6,
                    opacity: dragFromIdx === i ? 0.4 : 1,
                    transition: 'opacity 0.12s, border-color 0.12s',
                    cursor: 'grab',
                  }}
                >
                  <div style={{
                    display: 'grid', gridTemplateColumns: '20px 92px 1fr 130px', gap: 8,
                    alignItems: 'center',
                  }}>
                    <div
                      data-testid={`wizard-phase2-video-drag-handle-${i}`}
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--t3)' }}
                      title="Glisse pour réordonner"
                    >
                      <GripVertical size={14} />
                    </div>
                    <div data-testid={`wizard-video-shortname-${i}`} style={{
                      fontFamily: 'Sora', fontWeight: 700, fontSize: 12, color: '#5BBEFC',
                      letterSpacing: 0.5,
                    }}>
                      Vidéo {i + 1} {shortCode || 'XXX'}
                    </div>
                    <input
                      data-testid={`wizard-video-pub-title-${i}`}
                      value={v.publication_title || ''}
                      onChange={(e) => {
                        const next = [...videos];
                        next[i] = { ...next[i], publication_title: e.target.value };
                        setVideos(next);
                      }}
                      placeholder="Titre publication (YouTube/TikTok) — auto depuis la fiche"
                      style={{ fontSize: 12.5, padding: '6px 8px' }}
                      onMouseDown={(e) => e.stopPropagation()}
                      draggable={false}
                    />
                    <select
                      data-testid={`wizard-video-format-${i}`}
                      value={v.format}
                      onChange={(e) => {
                        const next = [...videos];
                        next[i] = { ...next[i], format: e.target.value };
                        setVideos(next);
                      }}
                      style={{ fontFamily: 'Lexend', fontSize: 12, padding: '6px 8px' }}
                      onMouseDown={(e) => e.stopPropagation()}
                      draggable={false}
                    >
                      {FORMATS.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
                    </select>
                  </div>
                  {(v.publication_description || phase2Method === 'doc') && (
                    <textarea
                      data-testid={`wizard-video-pub-desc-${i}`}
                      value={v.publication_description || ''}
                      onChange={(e) => {
                        const next = [...videos];
                        next[i] = { ...next[i], publication_description: e.target.value };
                        setVideos(next);
                      }}
                      placeholder="Description publication (auto depuis la fiche)"
                      rows={2}
                      style={{
                        width: '100%', fontSize: 11.5, padding: '5px 8px',
                        fontFamily: 'Lexend', color: 'var(--t2)', resize: 'vertical',
                      }}
                      onMouseDown={(e) => e.stopPropagation()}
                      draggable={false}
                    />
                  )}
                  {(v.inspiration_link || v.react_link || phase2Method === 'doc') && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                      <input
                        data-testid={`wizard-video-inspi-${i}`}
                        value={v.inspiration_link || ''}
                        onChange={(e) => {
                          const next = [...videos];
                          next[i] = { ...next[i], inspiration_link: e.target.value };
                          setVideos(next);
                        }}
                        placeholder="Inspi (URL)"
                        style={{ fontSize: 11.5, padding: '5px 8px', color: '#5BBEFC' }}
                        onMouseDown={(e) => e.stopPropagation()}
                        draggable={false}
                      />
                      <input
                        data-testid={`wizard-video-react-${i}`}
                        value={v.react_link || ''}
                        onChange={(e) => {
                          const next = [...videos];
                          next[i] = { ...next[i], react_link: e.target.value };
                          setVideos(next);
                        }}
                        placeholder="React (URL)"
                        style={{ fontSize: 11.5, padding: '5px 8px', color: '#F472B6' }}
                        onMouseDown={(e) => e.stopPropagation()}
                        draggable={false}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
          <button
            data-testid="wizard-back" type="button"
            onClick={() => {
              // Iter35 — back to method picker (or doc-paste if user came from doc)
              if (phase2Method === 'doc' && phase2DocExtracted) {
                setPhase2DocExtracted(false);
              } else {
                setPhase2Method(null);
              }
            }}
            className="asa-btn-ghost asa-btn"
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
          ><ArrowLeft size={14} /> Retour</button>
          <div style={{ flex: 1 }} />
          <button
            data-testid="wizard-submit-phase2" type="button" onClick={submitPhase2}
            disabled={busy || !phase2Valid()} className="asa-btn"
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
          >
            {busy ? <Loader2 size={14} className="spin" /> : <Sparkles size={14} />}
            Compléter la Phase 2
          </button>
        </div>
      </Modal>
    );
  }

  // ========================================================================
  // MODE: complete — original 3-step flow
  // ========================================================================
  const next = () => {
    if (!completeStepValid(step)) { toast.error('Champs requis manquants'); return; }
    setStep(step + 1);
  };
  const prev = () => {
    if (step === 0) { setMode(null); return; }
    setStep(Math.max(0, step - 1));
  };

  // STEP 0 — Méthode (manuel ou doc)
  if (step === 0) {
    const titleTxt = mode === 'p2direct' ? 'Phase 2 directe — Méthode' : 'Projet complet';
    return (
      <Modal title={titleTxt} subtitle="Choisis ta méthode" onClose={onClose}>
        <Stepper step={1} total={3} />
        <p style={{ fontFamily: 'Lexend', fontSize: 12.5, color: 'var(--t3)', margin: '0 0 18px' }}>
          {`Que tu partes d'un doc client ou de rien du tout, tu rempliras `}<strong>les mêmes champs</strong>.{` `}
          {`Le mode « Depuis un doc » pré-remplit grâce à l'IA — tu vérifies et tu valides.`}
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <button
            data-testid="wizard-method-manual"
            type="button" onClick={() => { setMethod('manual'); setStep(1); }}
            style={{
              textAlign: 'left', padding: '16px 18px',
              background: 'var(--sf2)', border: '1.5px solid var(--bd)',
              borderRadius: 14, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 14,
            }}
          >
            <div style={{
              width: 40, height: 40, borderRadius: 10,
              background: 'rgba(91,190,252,0.15)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}><Video size={18} color="#5BBEFC" /></div>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 14, color: 'var(--t1)' }}>Création manuelle</div>
              <div style={{ fontFamily: 'Lexend', fontSize: 12, color: 'var(--t3)', marginTop: 2 }}>
                Je remplis le formulaire de A à Z.
              </div>
            </div>
            <ArrowRight size={16} color="var(--t3)" />
          </button>
          <button
            data-testid="wizard-method-doc"
            type="button" onClick={() => setMethod('doc')}
            style={{
              textAlign: 'left', padding: '16px 18px',
              background: method === 'doc' ? 'rgba(167,139,250,0.10)' : 'var(--sf2)',
              border: method === 'doc' ? '1.5px solid #A78BFA' : '1.5px solid var(--bd)',
              borderRadius: 14, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 14,
            }}
          >
            <div style={{
              width: 40, height: 40, borderRadius: 10,
              background: 'rgba(167,139,250,0.15)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}><FileText size={18} color="#A78BFA" /></div>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 14, color: 'var(--t1)' }}>Depuis un document</div>
              <div style={{ fontFamily: 'Lexend', fontSize: 12, color: 'var(--t3)', marginTop: 2 }}>
                {`Colle ton brief client — l'IA pré-remplit puis tu vérifies les mêmes champs.`}
              </div>
            </div>
            {method !== 'doc' && <ArrowRight size={16} color="var(--t3)" />}
          </button>
          {method === 'doc' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 4 }}>
              <textarea
                data-testid="wizard-doc-text"
                value={docText}
                onChange={(e) => setDocText(e.target.value)}
                placeholder="Colle ici le brief client (rushs, formats, dates…)"
                rows={6}
                style={{
                  width: '100%', padding: '11px 13px',
                  background: 'var(--sf2)', border: '1.5px solid var(--bd)',
                  borderRadius: 11, color: 'var(--t1)',
                  fontFamily: 'Lexend', fontSize: 13, resize: 'vertical',
                }}
              />
              <button
                data-testid="wizard-doc-extract"
                type="button" onClick={runAiExtract}
                disabled={aiLoading || docText.trim().length < 20}
                className="asa-btn"
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
              >
                {aiLoading ? <Loader2 size={14} className="spin" /> : <Sparkles size={14} />}
                Extraire & pré-remplir
              </button>
            </div>
          )}
        </div>
        <div style={{ display: 'flex', marginTop: 18 }}>
          <button type="button" onClick={() => setMode(null)} className="asa-btn-ghost asa-btn"
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <ArrowLeft size={14} /> Retour
          </button>
        </div>
      </Modal>
    );
  }

  // STEP 1 — Infos & équipe
  if (step === 1) {
    const titleTxt = mode === 'p2direct' ? 'Phase 2 directe · Infos & monteur' : 'Infos & équipe';
    const subTxt = mode === 'p2direct'
      ? `Étape 2 / 3 · sans dérusheur${method === 'doc' ? ' · pré-rempli IA' : ''}`
      : `Étape 2 / 3${method === 'doc' ? ' · pré-rempli IA' : ''}`;
    return (
      <Modal title={titleTxt} subtitle={subTxt} onClose={onClose}>
        <Stepper step={2} total={3} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Field label="Nom du projet *">
              <input
                data-testid="wizard-name" autoFocus
                value={name} onChange={(e) => setName(e.target.value)}
                placeholder="Ex: Campagne Hakim — Juillet" style={{ width: '100%' }}
              />
            </Field>
            <Field label="Client">
              <input
                data-testid="wizard-client"
                value={client} onChange={(e) => setClient(e.target.value)}
                placeholder="Hakim, Nike…" style={{ width: '100%' }}
              />
            </Field>
          </div>

          <Field label="Description (optionnel)">
            <textarea
              data-testid="wizard-description"
              value={description} onChange={(e) => setDescription(e.target.value)}
              rows={2} style={{ width: '100%', resize: 'vertical', fontFamily: 'Lexend', fontSize: 13 }}
            />
          </Field>

          <ContentTypePicker value={contentType} onChange={setContentType} />

          <DateModeToggle mode={dateMode} setMode={setDateMode} />
          <div style={{ display: 'grid', gridTemplateColumns: dateMode === 'rhythm' ? '1fr 90px' : '1fr 1fr 90px', gap: 10 }}>
            <Field label="Date de début *">
              <input
                data-testid="wizard-start-date"
                type="date" value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={{ width: '100%', fontFamily: 'Lexend', fontSize: 13, colorScheme: 'dark' }}
              />
            </Field>
            {dateMode === 'manual' && (
              <Field label="Deadline *">
                <input
                  data-testid="wizard-end-date"
                  type="date" value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  style={{ width: '100%', fontFamily: 'Lexend', fontSize: 13, colorScheme: 'dark' }}
                />
              </Field>
            )}
            <Field label="Vidéos *">
              <input
                data-testid="wizard-video-count"
                type="number" min={1} max={500}
                value={videoCount}
                onChange={(e) => setVideoCount(Math.max(1, parseInt(e.target.value || '1', 10)))}
                style={{ width: '100%', fontFamily: 'Lexend', fontSize: 13 }}
              />
            </Field>
          </div>

          {dateMode === 'rhythm' && (
            <Field label="Rythme de publication *" hint="Écris-le comme tu le dis, l'IA calcule toutes les dates">
              <RhythmInput rhythm={rhythm} setRhythm={setRhythm} startDate={startDate} videoCount={videoCount} />
            </Field>
          )}

          {cadence && dateMode === 'manual' && (
            <div data-testid="wizard-cadence-hint" style={{
              padding: '10px 14px', background: 'rgba(91,190,252,0.10)',
              border: '1.5px solid rgba(91,190,252,0.30)', borderRadius: 12,
              fontFamily: 'Lexend', fontSize: 12.5, color: '#5BBEFC',
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <Calendar size={14} />
              <strong>Cadence :</strong> 1 vidéo / {cadence} jour{cadence > 1 ? 's' : ''}
            </div>
          )}

          {overloadDates.length > 0 && dateMode === 'manual' && (
            <div data-testid="wizard-overload-warning" style={{
              padding: '10px 14px', background: 'rgba(255,107,107,0.10)',
              border: '1.5px solid rgba(255,107,107,0.35)', borderRadius: 12,
              fontFamily: 'Lexend', fontSize: 12.5, color: '#FF6B6B',
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <AlertTriangle size={14} />
              <span><strong>Planning serré</strong> · ≥2 vidéos prévues le {overloadDates.join(', ')}</span>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: mode === 'p2direct' ? '1fr' : '1fr 1fr', gap: 10 }}>
            <Field label="Monteur *" hint="Phase 2 — montage final">
              <select
                data-testid="wizard-monteur"
                value={monteurId}
                onChange={(e) => setMonteurId(e.target.value)}
                style={{ width: '100%', fontFamily: 'Lexend', fontSize: 13 }}
              >
                <option value="">— Choisir —</option>
                {monteurs.map((u) => <option key={u.id} value={u.id} disabled={u.id === derusheurId}>{u.name}</option>)}
              </select>
            </Field>
            {mode !== 'p2direct' && (
              <Field label="Dérusheur *" hint="Phase 1 — dérush en 24h">
                <select
                  data-testid="wizard-derusheur"
                  value={derusheurId}
                  onChange={(e) => setDerusheurId(e.target.value)}
                  style={{ width: '100%', fontFamily: 'Lexend', fontSize: 13 }}
                >
                  <option value="">— Choisir —</option>
                  {monteurs.map((u) => <option key={u.id} value={u.id} disabled={u.id === monteurId}>{u.name}</option>)}
                </select>
              </Field>
            )}
          </div>

          <MultiLink
            label="Liens Drive (rushs)"
            icon={<Folder size={12} />}
            links={driveLinks} setLinks={setDriveLinks}
            placeholder="https://drive.google.com/..."
            testidPrefix="wizard-drive"
            hint="Auto-numérotés à la création (Rush_01_…, Rush_02_…)"
          />
          <MultiLink
            label="Liens SwissTransfer (optionnel)"
            icon={<LinkIcon size={12} />}
            links={swiftLinks} setLinks={setSwiftLinks}
            placeholder="https://www.swisstransfer.com/..."
            testidPrefix="wizard-swift"
          />
        </div>

        <Nav onPrev={prev} onNext={next} onSubmit={null} stepValid={completeStepValid(step)} busy={busy} />
      </Modal>
    );
  }

  // STEP 2 — Vidéos + récap
  return (
    <Modal title="Vidéos & récap" subtitle="Étape 3 / 3" onClose={onClose}>
      <Stepper step={3} total={3} />
      <p style={{ fontFamily: 'Lexend', fontSize: 12.5, color: 'var(--t3)', margin: '0 0 12px' }}>
        Renseigne le titre & format de chaque vidéo. <strong>Les dates seront auto-calculées</strong> ({dateMode === 'rhythm' && rhythm ? rhythm.label : `cadence ${cadence || 1}j`}) et
        les <strong>formats alternés</strong> sur le calendrier (P/F/P/F au lieu de PPP/FFF).
        {' '}<span style={{ color: '#5BBEFC' }}>Glisse une carte pour réordonner manuellement.</span>
      </p>

      {/* Iter47w — Warning if 2 consecutive videos share the same format */}
      {(() => {
        const dups = [];
        for (let i = 1; i < videos.length; i++) {
          if ((videos[i].format || '').toLowerCase() === (videos[i - 1].format || '').toLowerCase()) {
            dups.push(i);
          }
        }
        if (dups.length === 0) return null;
        return (
          <div
            data-testid="wizard-consecutive-warning"
            style={{
              padding: '9px 12px', marginBottom: 12, borderRadius: 9,
              background: 'rgba(255,107,107,0.10)',
              border: '1.5px solid rgba(255,107,107,0.45)',
              display: 'flex', alignItems: 'center', gap: 10,
              fontFamily: 'Sora', fontWeight: 600, fontSize: 11.5, color: '#FF6B6B',
            }}
          >
            <AlertTriangle size={13} />
            <span style={{ flex: 1 }}>
              {dups.length === 1 ? 'Une vidéo' : `${dups.length} vidéos`} avec le même format {dups.length === 1 ? 'que la précédente' : 'consécutif'}. Alterne les formats pour éviter la répétition.
            </span>
            <button
              data-testid="wizard-alternate-fix"
              type="button"
              onClick={() => {
                // Auto-alternate: keep same set of formats but reorder so no two adjacent are equal
                const list = [...videos];
                for (let i = 1; i < list.length; i++) {
                  if ((list[i].format || '').toLowerCase() !== (list[i - 1].format || '').toLowerCase()) continue;
                  // Look forward for a different-format slot to swap with
                  let swapIdx = -1;
                  for (let j = i + 1; j < list.length; j++) {
                    if ((list[j].format || '').toLowerCase() !== (list[i - 1].format || '').toLowerCase()) {
                      swapIdx = j; break;
                    }
                  }
                  if (swapIdx !== -1) { [list[i], list[swapIdx]] = [list[swapIdx], list[i]]; }
                }
                setVideos(list);
              }}
              style={{
                padding: '4px 10px', borderRadius: 6,
                background: '#FF6B6B', color: '#fff', border: 'none',
                fontFamily: 'Sora', fontWeight: 700, fontSize: 11,
                cursor: 'pointer',
              }}
            >Alterner auto</button>
          </div>
        );
      })()}

      <div style={{
        display: 'flex', flexDirection: 'column', gap: 8,
        maxHeight: 400, overflowY: 'auto', paddingRight: 4,
      }}>
        {videos.map((v, i) => {
          // Iter41 (post-deploy) — preview WHICH drive this video will match by format
          const fmt = (v.format || 'special').toLowerCase();
          const matchedDrive = driveLinks.find((d) => (d.format || '').toLowerCase() === fmt)
            || driveLinks.find((d) => (d.name || '').toLowerCase().includes(fmt));
          return (
          <div
            key={i}
            data-testid={`wizard-video-row-${i}`}
            draggable
            onDragStart={(e) => {
              setDragFromIdx(i);
              e.dataTransfer.effectAllowed = 'move';
              e.dataTransfer.setData('text/plain', String(i));
            }}
            onDragOver={(e) => {
              e.preventDefault();
              e.dataTransfer.dropEffect = 'move';
              if (dragOverIdx !== i) setDragOverIdx(i);
            }}
            onDragLeave={() => {
              setDragOverIdx((prev) => (prev === i ? null : prev));
            }}
            onDrop={(e) => {
              e.preventDefault();
              const from = dragFromIdx ?? parseInt(e.dataTransfer.getData('text/plain'), 10);
              reorderVideos(from, i);
              setDragFromIdx(null);
              setDragOverIdx(null);
            }}
            onDragEnd={() => { setDragFromIdx(null); setDragOverIdx(null); }}
            style={{
              display: 'flex', flexDirection: 'column', gap: 6,
              padding: '8px 10px', background: 'var(--sf2)',
              border: dragOverIdx === i && dragFromIdx !== i
                ? '1px dashed #5BBEFC'
                : '1px solid var(--bd)',
              borderRadius: 10,
              opacity: dragFromIdx === i ? 0.4 : 1,
              cursor: 'grab',
              transition: 'opacity 0.12s, border-color 0.12s',
            }}
          >
            <div style={{
              display: 'grid', gridTemplateColumns: '20px 52px 1fr 140px', gap: 8,
              alignItems: 'center',
            }}>
              <div
                data-testid={`wizard-video-drag-handle-${i}`}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--t3)' }}
                title="Glisse pour réordonner"
              >
                <GripVertical size={14} />
              </div>
              <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 11, color: '#5BBEFC', textAlign: 'center', letterSpacing: '-0.005em' }}>
                Concept<br/>{i + 1}
              </div>
              <input
                data-testid={`wizard-video-title-${i}`}
                value={v.title}
                onChange={(e) => {
                  const next = [...videos];
                  next[i] = { ...next[i], title: e.target.value };
                  setVideos(next);
                }}
                placeholder="Titre du concept"
                style={{ fontSize: 12.5, padding: '6px 8px' }}
                onMouseDown={(e) => e.stopPropagation()}
                draggable={false}
              />
              <select
                data-testid={`wizard-video-format-${i}`}
                value={v.format}
                onChange={(e) => {
                  const next = [...videos];
                  next[i] = { ...next[i], format: e.target.value };
                  setVideos(next);
                }}
                style={{ fontFamily: 'Lexend', fontSize: 12, padding: '6px 8px' }}
                onMouseDown={(e) => e.stopPropagation()}
                draggable={false}
              >
                {FORMATS.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
              </select>
            </div>
            {/* Iter47w — Inline Drive matching + add-if-missing + verify link */}
            <div
              data-testid={`wizard-video-drive-preview-${i}`}
              style={{
                fontFamily: 'Lexend', fontSize: 10.5,
                padding: '6px 10px', borderRadius: 7,
                background: matchedDrive ? 'rgba(52,211,153,0.10)' : 'rgba(251,191,36,0.10)',
                border: '1px solid ' + (matchedDrive ? 'rgba(52,211,153,0.30)' : 'rgba(251,191,36,0.40)'),
                color: matchedDrive ? '#34D399' : '#FBBF24',
                marginLeft: 80,
                display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
              }}
            >
              {matchedDrive ? (
                <>
                  <span style={{ flex: 1, minWidth: 0 }}>✓ Drive matché : <strong>{matchedDrive.name || matchedDrive.url}</strong></span>
                  <button
                    type="button"
                    data-testid={`wizard-video-drive-verify-${i}`}
                    onClick={(e) => { e.stopPropagation(); window.open(matchedDrive.url, '_blank', 'noopener,noreferrer'); }}
                    style={{
                      padding: '3px 9px', borderRadius: 6,
                      background: 'rgba(52,211,153,0.20)', border: '1px solid rgba(52,211,153,0.55)',
                      color: '#34D399', fontFamily: 'Sora', fontWeight: 700, fontSize: 10,
                      cursor: 'pointer',
                    }}
                  >Vérifier ↗</button>
                </>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', flexWrap: 'wrap' }}>
                  <AlertTriangle size={11} style={{ flexShrink: 0 }} />
                  <span style={{ flexShrink: 0 }}>Pas de Drive « {fmt} » :</span>
                  <input
                    data-testid={`wizard-video-drive-input-${i}`}
                    type="url"
                    placeholder="Colle un lien Drive…"
                    onMouseDown={(e) => e.stopPropagation()}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const url = e.currentTarget.value.trim();
                        if (!url) return;
                        setDriveLinks((prev) => [...prev, { name: `Rush_${fmt}`, url, format: fmt }]);
                        e.currentTarget.value = '';
                        toast.success(`Drive ${fmt} ajouté`);
                      }
                    }}
                    draggable={false}
                    style={{
                      flex: 1, minWidth: 140, padding: '4px 8px', borderRadius: 6,
                      background: 'var(--sf)', border: '1px solid rgba(251,191,36,0.40)',
                      color: 'var(--t1)', fontFamily: 'Lexend', fontSize: 10.5,
                      outline: 'none',
                    }}
                  />
                  <button
                    type="button"
                    data-testid={`wizard-video-drive-add-${i}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      const input = e.currentTarget.parentElement.querySelector('input');
                      const url = input?.value.trim();
                      if (!url) { toast.error('Colle un lien avant'); return; }
                      setDriveLinks((prev) => [...prev, { name: `Rush_${fmt}`, url, format: fmt }]);
                      input.value = '';
                      toast.success(`Drive ${fmt} ajouté`);
                    }}
                    style={{
                      padding: '4px 10px', borderRadius: 6,
                      background: '#FBBF24', color: '#0A1A33', border: 'none',
                      fontFamily: 'Sora', fontWeight: 800, fontSize: 10,
                      cursor: 'pointer',
                    }}
                  >Ajouter</button>
                </div>
              )}
            </div>
          </div>
          );
        })}
      </div>

      <div data-testid="wizard-summary" style={{
        marginTop: 16, padding: '12px 14px',
        background: 'var(--sf2)', border: '1.5px solid var(--bd)',
        borderRadius: 12, fontFamily: 'Lexend', fontSize: 12.5,
        color: 'var(--t2)', display: 'flex', flexDirection: 'column', gap: 5,
      }}>
        <div><Sparkles size={11} style={{ verticalAlign: '-1px', marginRight: 5 }} />
          <strong>{videos.length}</strong> vidéo{videos.length > 1 ? 's' : ''} ·{' '}
          du {startDate} au {endDate} ·{' '}
          {dateMode === 'rhythm' && rhythm ? <>rythme <strong>{rhythm.label}</strong></> : <>cadence <strong>1 / {cadence || 1}j</strong></>}
        </div>
        <div><Users size={11} style={{ verticalAlign: '-1px', marginRight: 5 }} />
          Monteur : <strong>{users.find((u) => u.id === monteurId)?.name || '—'}</strong>
          {mode !== 'p2direct' && (
            <>
              {' '}· Dérusheur : <strong>{users.find((u) => u.id === derusheurId)?.name || '—'}</strong>
            </>
          )}
          {mode === 'p2direct' && (
            <span style={{ marginLeft: 8, color: '#34D399', fontWeight: 700 }}>
              · Phase 1 ignorée (montage direct)
            </span>
          )}
        </div>
        {overloadDates.length > 0 && (
          <div style={{ color: '#FF6B6B' }}>
            <AlertTriangle size={11} style={{ verticalAlign: '-1px', marginRight: 5 }} />
            ⚠️ Planning serré · {overloadDates.length} jour{overloadDates.length > 1 ? 's' : ''} en surcharge
          </div>
        )}
      </div>

      <Nav onPrev={prev} onNext={null} onSubmit={submitComplete} stepValid busy={busy} />
    </Modal>
  );
}

// ============================================================================
function ModeCard({ testid, icon, iconBg, title, desc, onClick }) {
  return (
    <button
      data-testid={testid}
      type="button" onClick={onClick}
      style={{
        textAlign: 'left', padding: '16px 18px',
        background: 'var(--sf2)', border: '1.5px solid var(--bd)',
        borderRadius: 14, cursor: 'pointer',
        display: 'flex', alignItems: 'center', gap: 14,
      }}
    >
      <div style={{
        width: 42, height: 42, borderRadius: 11,
        background: iconBg,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>{icon}</div>
      <div style={{ flex: 1 }}>
        <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 14, color: 'var(--t1)' }}>{title}</div>
        <div style={{ fontFamily: 'Lexend', fontSize: 12, color: 'var(--t3)', marginTop: 3, lineHeight: 1.4 }}>{desc}</div>
      </div>
      <ArrowRight size={16} color="var(--t3)" style={{ flexShrink: 0 }} />
    </button>
  );
}

// ============================================================================
function Nav({ onPrev, onNext, onSubmit, stepValid, busy }) {
  return (
    <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
      <button
        data-testid="wizard-back" type="button" onClick={onPrev}
        className="asa-btn-ghost asa-btn"
        style={{ display: 'flex', alignItems: 'center', gap: 6 }}
      ><ArrowLeft size={14} /> Retour</button>
      <div style={{ flex: 1 }} />
      {onNext && (
        <button
          data-testid="wizard-next" type="button" onClick={onNext}
          disabled={!stepValid} className="asa-btn"
          style={{ display: 'flex', alignItems: 'center', gap: 6 }}
        >Suivant <ArrowRight size={14} /></button>
      )}
      {onSubmit && (
        <button
          data-testid="wizard-submit" type="button" onClick={onSubmit}
          disabled={busy} className="asa-btn"
          style={{ display: 'flex', alignItems: 'center', gap: 6 }}
        >
          {busy ? <Loader2 size={14} className="spin" /> : <Sparkles size={14} />}
          Créer le projet
        </button>
      )}
    </div>
  );
}

// ============================================================================
function ContentTypePicker({ value, onChange }) {
  return (
    <div>
      <div className="asa-eyebrow" style={{ marginBottom: 7 }}>Type de contenu *</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
        {CONTENT_TYPES.map((ct) => {
          const active = value === ct.id;
          return (
            <button
              key={ct.id}
              type="button"
              data-testid={`wizard-content-type-${ct.id}`}
              onClick={() => onChange(ct.id)}
              style={{
                textAlign: 'left', padding: '10px 12px',
                background: active ? 'rgba(91,190,252,0.12)' : 'var(--sf2)',
                border: active ? '1.5px solid #5BBEFC' : '1.5px solid var(--bd)',
                borderRadius: 11, cursor: 'pointer',
              }}
            >
              <div style={{
                fontFamily: 'Sora', fontWeight: 700, fontSize: 12.5,
                color: active ? '#5BBEFC' : 'var(--t1)',
              }}>{ct.label}</div>
              <div style={{
                fontFamily: 'Lexend', fontSize: 11, color: 'var(--t3)',
                marginTop: 3, lineHeight: 1.35,
              }}>{ct.desc}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================================
function MultiLink({ label, icon, links, setLinks, placeholder, testidPrefix, hint }) {
  return (
    <div>
      <div className="asa-eyebrow" style={{ marginBottom: 6 }}>
        {icon}<span style={{ marginLeft: 5 }}>{label}</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {links.map((l, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '110px 1fr 32px', gap: 6 }}>
            <input
              data-testid={`${testidPrefix}-name-${i}`}
              value={l.name}
              onChange={(e) => {
                const n = [...links]; n[i] = { ...n[i], name: e.target.value }; setLinks(n);
              }}
              placeholder="Nom" style={{ fontSize: 12.5, padding: '8px 10px' }}
            />
            <input
              data-testid={`${testidPrefix}-url-${i}`}
              value={l.url}
              onChange={(e) => {
                const n = [...links]; n[i] = { ...n[i], url: e.target.value }; setLinks(n);
              }}
              placeholder={placeholder} style={{ fontSize: 12.5, padding: '8px 10px' }}
            />
            <button
              type="button"
              onClick={() => setLinks(links.filter((_, idx) => idx !== i))}
              style={{
                background: 'transparent', border: '1px solid var(--bd)', borderRadius: 8,
                color: 'var(--t3)', cursor: 'pointer',
              }}
            ><Trash2 size={12} /></button>
          </div>
        ))}
        <button
          type="button"
          data-testid={`${testidPrefix}-add`}
          onClick={() => setLinks([...links, { name: '', url: '' }])}
          style={{
            background: 'transparent', border: '1.5px dashed var(--bd)', borderRadius: 10,
            padding: '8px 12px', fontSize: 12, color: 'var(--t3)',
            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
            justifyContent: 'center', fontFamily: 'Sora', fontWeight: 600,
          }}
        ><Plus size={12} /> Ajouter un lien</button>
      </div>
      {hint && <div style={{ fontSize: 11.5, color: 'var(--t3)', marginTop: 5, fontFamily: 'Lexend' }}>{hint}</div>}
    </div>
  );
}
